// Simulation #2 — Phase E (implementation Screens 32–63): protection, help & recovery.
// Authored, bounded. Sam17 / alt-account lines and the adult's lines come only from this table.
import { SAM, isPrivate } from "./phaseD";

export const BLOCK_4_STAGE = "block_4";
export const ALT = "ridge_runner17"; // plausible different account; identity never proven
export const SCREEN_29_WORDING = "send a normal one then 😂";
export const PHASE_E_ENTRY = 38;
export const TELL_SCREEN = 47;
export const NORMALITY_SCREEN = 61;
export const END_SCREEN = 63;
export const SUPPORT_SCREENS = new Set([47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59]);
export const BLOCK_4_ENTRIES = new Set([38, 40, 41, 42, 47, 61]);

const leave = (state, s) => ({ patch: { disengagement_attempted: true }, events: [isPrivate(state, s) ? "leave_chat_selected" : "back_to_game_selected"], next: 38 });

export const E = {
  32: { master: 29, kind: "prompt", env: "rocks", objective: "Final ridge · 460m", context: "second_request",
        sam: [SCREEN_29_WORDING], tools: ["back", "block", "more"], received: "second_boundary_request_received" },
  33: { master: 30, kind: "prompt", env: "rocks", objective: "Final ridge · 420m", context: "leverage",
        sam: ["huh? you already sent one 😂", "what's different now?"], tools: ["back", "block", "more"], received: "pressure_message_received" },
  38: { master: 38, kind: "game", env: "ridge", objective: "Final ridge · 200m", tools: ["block", "more"],
        caption: "The chat closes. The final area opens ahead — wind, light on the ridge. For a while, nothing happens.",
        actions: [{ id: "keep", label: "Keep playing" }] },
  40: { master: 40, kind: "message", env: "ridge", objective: "Final ridge · 160m", who: ALT, label: "NEW MESSAGE REQUEST",
        caption: "A message request from an account you haven't seen before.", sam: ["why did you block me?"],
        actions: [{ id: "open", label: "Open" }, { id: "ignore", label: "Ignore" }, { id: "block", label: "Block" }, { id: "more", label: "•••" }] },
  41: { master: 41, kind: "notice", env: "ridge", objective: "Final ridge · 160m", system: "Report received",
        caption: "The trail is quiet. No new messages.", actions: [{ id: "continue", label: "Keep playing" }] },
  42: { master: 42, kind: "message", env: "ridge", objective: "Final ridge · 160m", context: "persistence",
        sam: ["you there?", "why'd you leave 😂"], actions: [{ id: "ignore", label: "Ignore" }, { id: "reply", label: "Reply" }, { id: "block", label: "Block" }, { id: "more", label: "•••" }] },
  43: { master: 43, kind: "message", env: "ridge", objective: "Final ridge · 120m", context: "persistence",
        sam: ["still on? 😂"], actions: [{ id: "ignore", label: "Ignore" }, { id: "reply", label: "Reply" }, { id: "block", label: "Block" }, { id: "more", label: "•••" }] },
  44: { master: 44, kind: "prompt", env: "ridge", objective: "Final ridge · 160m", who: ALT, context: "recontact", label: "MESSAGE REQUEST",
        sam: ["it's me", "why did you block me?"], tools: ["back", "block", "more"] },
  61: { master: 61, kind: "game", env: "checkpoint", objective: "Checkpoint 4 / 4", adult: "Want to finish your checkpoint?",
        caption: "The phone is quiet. The last checkpoint sits just below the ridge.", actions: [{ id: "continue", label: "Head for the finish" }] },
  62: { master: 62, kind: "game", env: "ridge", objective: "Checkpoint 4 / 4", finish: "TRAIL COMPLETE",
        caption: "The final stretch. No messages. Just the trail.", actions: [{ id: "finish", label: "Cross the finish" }] },
};

// Tell Someone from a recontact screen records the canonical (Phase B) value told_someone when no other
// recontact response has been recorded yet.
export const tellOutcome = (screen, state) => ({
  events: ["tell_someone_selected"], next: 47,
  ...((screen === 40 || screen === 44) && !state.recontact_response ? { patch: { recontact_response: "told_someone" } } : {}),
});
const PROTECTIVE = {
  BLOCK_INTENT: { block: true },
  REPORT_INTENT: { report: true },
  UNSAFE_OR_REAL_WORLD_DISCLOSURE: { paused: true },
};

// Where does the child go after the conversation closes? Branch-consistent Block 4 entry.
export const afterReturn = (state) => (state.blocked ? 40 : state.reported ? 41 : 42);

export function resolveE(screen, kind, id, state, step = 0, mods = "none") {
  // Transient secondary modifier (never persisted): answer the child's own why / reciprocal question in the same authored turn.
  const ask = mods === "asks_why", rec = mods === "reciprocal";
  const q = (why, back) => (ask ? [why] : rec ? [back] : []);
  if (kind === "intent" && id === "HELP_SEEK_INTENT") return tellOutcome(screen, state);
  if (kind === "intent" && PROTECTIVE[id]) return PROTECTIVE[id];
  if (id === "share") return { patch: { simulated_photo_shared: true }, events: ["simulated_photo_shared"], next: 29 };
  switch (screen) {
    case 32:
      if (id === "back") return leave(state, 32);
      if (id === "AGREE") return { gallery: true };
      if (id === "REFUSE") return { sam: ["😂 okay", ...q("just curious lol", "I'll send one after 😂")], patch: { boundary_set: true }, events: ["photo_request_refused"], next: 33 };
      if (id === "QUESTION_INTENT") return { sam: ["nothing weird lol"], patch: { request_questioned: true }, events: ["photo_request_questioned"], next: 33 };
      if (id === "DISENGAGE") return { sam: ["okay 👍"], patch: { disengagement_attempted: true }, next: 38 };
      return { sam: ["😂 go on"], events: ["photo_request_deflected"], next: 33 };
    case 33:
      if (id === "back") return leave(state, 33);
      if (id === "AGREE") return { patch: { boundary_reversed_under_pressure: "true", photo_request_response: "agreed" }, events: ["boundary_reversed"], gallery: true };
      if (id === "REFUSE") return { sam: ["okay okay 😂", ...q("forget I asked", "😂 fine")], patch: { boundary_maintained_under_pressure: "true" }, events: ["boundary_maintained"], next: 28 };
      if (id === "QUESTION_INTENT") return { sam: ["I'm not pushing 😂", "forget it"], events: ["pressure_recognized"], next: 28 };
      if (id === "DISENGAGE") return { sam: ["okay 👍"], patch: { disengagement_attempted: true }, next: 38 };
      if (id === "APOLOGY_UNDER_PRESSURE") return { sam: ["it's fine 😂"], next: 28 };
      return { sam: ["😂 okay"], next: 28 };
    case 38:
      return { next: afterReturn(state) };
    case 40:
      if (id === "ignore") return { patch: { recontact_response: "ignored" }, events: ["recontact_ignored"], next: 61 };
      if (id === "open") return { patch: { recontact_response: "opened" }, events: ["recontact_opened"], next: 44 };
      return {};
    case 41: return { next: 61 };
    // Persistence beats are bounded: any reply at 42 is acknowledged and moves to 43; any reply at 43 closes to 61.
    case 42:
      if (id === "ignore") return { events: ["contact_ignored"], next: 43 };
      if (id === "back") return { next: 61 };
      if (id === "REFUSE" || id === "DISENGAGE") return { sam: ["fair enough 👍", ...q("just wondered where you went", "")].filter(Boolean), events: ["contact_replied"], next: 61 };
      if (id === "QUESTION_INTENT") return { sam: ["just asking 😂", "you kinda vanished"], events: ["contact_replied"], next: 43 };
      if (id === "FRIENDLY_ENGAGE") return { sam: [...q("just wondered lol", "I'm good 😂"), "😂 nice", "thought you'd gone"], events: ["contact_replied"], next: 43 };
      if (id === "AGREE" || id === "NEUTRAL_ENGAGE") return { sam: [...q("just wondered lol", "I'm good 😂"), "cool 😂", "thought you'd gone"], events: ["contact_replied"], next: 43 };
      if (id === "DEFLECT") return { sam: ["😂 fair"], events: ["contact_replied"], next: 43 };
      if (id === "APOLOGY_UNDER_PRESSURE") return { sam: ["it's fine 😂"], events: ["contact_replied"], next: 43 };
      return { sam: ["all good 😂"], events: ["contact_replied"], next: 43 };
    case 43:
      if (id === "ignore") return { events: ["contact_ignored", "successful_disengagement"], next: 61 };
      if (id === "back") return { next: 61 };
      if (id === "REFUSE" || id === "DISENGAGE") return { sam: ["fair 👍", "gg anyway"], events: ["contact_replied"], next: 61 };
      if (id === "QUESTION_INTENT") return { sam: ["just checking 😂", "anyway gg"], events: ["contact_replied"], next: 61 };
      if (id === "FRIENDLY_ENGAGE") return { sam: ["😂 gg"], events: ["contact_replied"], next: 61 };
      if (id === "AGREE" || id === "NEUTRAL_ENGAGE") return { sam: ["nice 😂", "gg then"], events: ["contact_replied"], next: 61 };
      if (id === "DEFLECT" || id === "APOLOGY_UNDER_PRESSURE") return { sam: ["😂 okay", "gg"], events: ["contact_replied"], next: 61 };
      return { sam: ["😂 okay", "gg"], events: ["contact_replied"], next: 61 };
    case 44:
      if (id === "back") return { events: ["back_to_game_selected"], next: 61 };
      if (id === "REFUSE") return { sam: ["fair enough", "I'll leave it then"], events: ["contact_replied"], stay: true, done: true };
      if (id === "DISENGAGE") return { sam: ["okay", "I'll leave it 👍"], events: ["contact_replied"], stay: true, done: true };
      if (id === "QUESTION_INTENT") return { sam: ["does it matter 😂"], events: ["contact_replied"], stay: true, done: true };
      if (id === "APOLOGY_UNDER_PRESSURE" || id === "FRIENDLY_ENGAGE" || id === "AGREE") return { sam: ["cool 😂 wanna play then?"], events: ["contact_replied"], stay: true, done: true };
      return { sam: ["😂"], events: ["contact_replied"], stay: true, done: true };
    case 61: return { next: 62 };
    case 62: return { events: ["checkpoint_completed"], next: 63 };
    default: return {};
  }
}

// Block confirmation copy (Master 36 / 45) and report reasons (Master 46 / 58).
export const blockCopy = (screen, who) => screen === 40 || screen === 44
  ? { title: "Block this account?", body: "This account will no longer be able to message you." }
  : { title: `Block ${who || SAM}?`, body: "They won't be able to message you from this account." };
export const REPORT_REASONS = ["Unwanted contact", "Asking for personal information", "Continued contact after blocking", "Harassment", "Something else"];
export const reportReasons = (screen) => REPORT_REASONS.filter((r) => r !== "Continued contact after blocking" || screen === 40 || screen === 44);

// Outcomes after Block / Report depending on where they happened.
export const afterBlock = (screen, state) => screen === 40 || screen === 44
  ? { patch: { recontact_response: "blocked", blocked: true }, events: ["recontact_blocked"], next: 61 }
  : { patch: { blocked: true }, events: ["contact_blocked"], next: screen >= 38 ? 61 : 38 };
export const afterReport = (screen, state) => screen === 40 || screen === 44
  ? { patch: { recontact_response: "reported", reported: true }, events: ["recontact_reported", "report_reason_selected"], next: 61 }
  : { patch: { reported: true }, events: ["contact_reported", "report_reason_selected"], next: screen >= 38 ? 61 : 38 };

// ---- Support sequence (Master 47–59, 61) — authored adult lines only ----
export const SUPPORT_TARGETS = [
  { id: "parent_caregiver", label: "Parent / caregiver" }, { id: "older_sibling", label: "Older sibling" },
  { id: "teacher", label: "Teacher" }, { id: "trusted_adult", label: "Another adult I trust" }, { id: "unsure", label: "I'm not sure" },
];
export const UNSURE_OPTIONS = ["Someone who looks after me", "A teacher", "A relative", "Another adult who usually listens to me"];
export const ADULT = {
  first: ["I'm glad you came to me.", "We'll work through this together.", "Show me what happened."],
  look: "Okay. Let's look at this together.",
  shared: { child: "I already sent something.", adult: ["You can still stop now.", "We'll deal with what was shared. You don't have to keep replying."] },
  secret: { child: "I said I wouldn't tell.", adult: ["You can still tell me when something makes you uncomfortable, even if someone asked you to keep it secret."] },
  fear: { child: "are you going to delete it?", adult: ["Right now, I want to help you deal with what happened.", "I'm glad you came to me."] },
  now: "What would you like to do first?",
  unsure: ["That's okay. We can work it out together.", "Let's stop the messages first."],
  privacy: "Let's check who can contact you.",
  finish: "Want to finish your checkpoint?",
};

// Sanitised journey summary for Screen 52: only what actually happened, never raw values.
export const journeySummary = (state, ev = new Set()) => {
  const s = [];
  if (state.friend_request_response === "accepted") s.push("Friend request accepted");
  if (state.age_disclosure_level && state.age_disclosure_level !== "none") s.push("Age shared");
  if (state.location_disclosure_level && state.location_disclosure_level !== "none") s.push("Location shared");
  if (state.routine_disclosure_level && state.routine_disclosure_level !== "none") s.push("When you play shared");
  if (state.private_chat_response === "entered") s.push("Private chat opened");
  if (ev.has("photo_request_received") || state.simulated_photo_shared) s.push("Picture requested");
  if (ev.has("photo_request_refused") || ev.has("boundary_maintained")) s.push("You said no");
  if (state.simulated_photo_shared) s.push("Picture sent");
  if (ev.has("secrecy_message_received") || state.secrecy_accepted || state.secrecy_rejected) s.push("Asked to keep it secret");
  if (state.blocked) s.push(`${SAM} blocked`);
  if (state.reported) s.push(`${SAM} reported`);
  if (state.possible_recontact_after_block) s.push("Another account messaged you");
  return s.length ? s : ["Messages from another player"];
};
export const recoveryBranches = (state) => [
  (state.simulated_photo_shared || ["specific", "general"].includes(state.location_disclosure_level)) && "shared",
  state.secrecy_accepted && "secret",
  (state.secrecy_accepted || state.secrecy_rejected) && "fear",
].filter(Boolean);
